import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { useQueryAssetById } from '@hooks/assets/useQueryAssetById';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetActiveCustomPolygons,
	resetActiveMarkers,
	setActiveAssetMarker,
	setInfoWinPosition,
	setMarkerForShortInfo,
	setShowMarkerFullInfo,
} from '@slices/map';

import { isEmpty, isEqual, shortenNumber } from '@utils/common';
import { getOoklaServiceColor } from '@utils/ookla/getOoklaServiceColor';
import { getClusterStyle } from '../helpers/getClusterStyles';

import { TAsset } from '@interfaces/assets/asset';

import { EColorAssetsBy, EMapMode, EMarkerTypes } from '@enums/map';

import { EOoklaDisplayTypes, TOoklaServiceLevel } from '@constants/ookla';
import { ASSET_ICONS } from '../constants';

import styles from '../Markers.module.scss';

interface IAssetMarkerProps {
	marker: TAsset;
	handleClickOnCluster: (marker: TAsset) => void;
}

type ClickHandler = ({ marker, x, y }: { marker: Partial<TAsset>; x: number; y: number }) => void

const AssetMarker = ({
	marker,
	handleClickOnCluster,
}: IAssetMarkerProps) => {
	const {
		activeAssetMarkers,
		assetIdsInsidePolygons,
		assetTypesSubtypes,
		colorAssetsBy,
		editingAsset,
		isShiftHeld,
		mapMode,
		showMarkerFullInfo,
		showMarkerShortInfo,
		ooklaFilteredData,
		appliedOoklaFilters,
	} = useAppSelector(map);
	const dispatch = useAppDispatch();

	const [isOoklaAvailable, setIsOoklaAvailable] = useState(false);
	const [ooklaServiceLevel, setOoklaServiceLevel] = useState<TOoklaServiceLevel>();

	const { prefetchAsset } = useQueryAssetById();

	const markerRef: React.Ref<HTMLDivElement> = useRef(null);

	const isCluster = marker.markerType !== EMarkerTypes.point && !!marker.count;
	const isActiveMarker = (
		!isCluster &&
		(showMarkerFullInfo || showMarkerShortInfo) &&
		(!!activeAssetMarkers.find((id) => id === marker.id) || editingAsset?.id === marker.id)
	);
	const isMarkerInsideActivePolygon = !!assetIdsInsidePolygons?.find((id) => id === marker.id);

	const colorByStatus = colorAssetsBy !== EColorAssetsBy.none && ((marker as never)[colorAssetsBy] as any);

	const clusterCount = isCluster ?? marker.count ? shortenNumber(marker.count) : '';
	const clusterCountStyle = isCluster && clusterCount && getClusterStyle(clusterCount);

	const assetIconUrl = assetTypesSubtypes && assetTypesSubtypes![marker.assetType?.assetTypeName] && (
		assetTypesSubtypes[marker.assetType?.assetTypeName]?.type?.icon?.downloadUrl
	);

	const backgroundImage = !isCluster
		? `url("${assetIconUrl || ASSET_ICONS.UNKNOWN}")`
		: 'unset';

	useEffect(() => {
		const { isOoklaInfo, type } = appliedOoklaFilters;

		setIsOoklaAvailable(!!isOoklaInfo && type === EOoklaDisplayTypes.colorRelatedAssets);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appliedOoklaFilters.isOoklaInfo, appliedOoklaFilters.type]);

	useEffect(() => {
		const { assetRelatedData } = ooklaFilteredData;

		if (!isOoklaAvailable || !marker?.assetId || isEmpty(assetRelatedData)) {
			setOoklaServiceLevel(undefined);

			return;
		}

		const ooklaData = assetRelatedData[marker.assetId];

		if (!ooklaData) {
			setOoklaServiceLevel(undefined);

			return;
		}

		const { level } = getOoklaServiceColor(ooklaData);

		setOoklaServiceLevel(level);
	}, [isActiveMarker, isOoklaAvailable, marker?.assetId, ooklaFilteredData]);

	const handleClickAsset = () => {
		if (mapMode === EMapMode.addMarker) return;

		if (isCluster) {
			handleClickOnCluster(marker);

			return;
		}

		dispatch(setShowMarkerFullInfo(true));
		dispatch(setMarkerForShortInfo(null));
		dispatch(resetActiveCustomPolygons());
		dispatch(setInfoWinPosition(null));
		dispatch(setActiveAssetMarker({
			asset: marker,
			isShift: isShiftHeld,
		}));
	};

	const handleRightClickMarker: ClickHandler = ({ marker, x, y }) => {
		if (isShiftHeld) return;

		dispatch(setInfoWinPosition({ x, y }));
		dispatch(setMarkerForShortInfo(marker));

		if (!isCluster) {
			dispatch(setActiveAssetMarker({
				asset: marker as TAsset,
				isShift: isShiftHeld,
				showMarkerShortInfo: true,
			}));
		}
	};

	const onMouseEnter = () => {
		if (!isCluster) void prefetchAsset(marker.id);
	};

	const handlerOnClickOutside = useCallback(() => {
		if (!isShiftHeld) {
			dispatch(setMarkerForShortInfo(null));
			dispatch(setInfoWinPosition(null));
			dispatch(resetActiveMarkers());
		}
	}, [dispatch, isShiftHeld]);

	useOnClickOutside({
		refs: [markerRef],
		handler: handlerOnClickOutside,
		disableClickOutside: false,
		triggerMouseButton: 2,
	});

	const markerClassname = cn(styles[colorByStatus], {
		[styles.activeCustomMarker]: isActiveMarker || isMarkerInsideActivePolygon,
		[styles[`${colorByStatus}_ACTIVE`]]: (isActiveMarker || isMarkerInsideActivePolygon) && !isOoklaAvailable,
		[styles[ooklaServiceLevel as string]]: isOoklaAvailable && !!ooklaServiceLevel,
		// eslint-disable-next-line max-len
		[styles[`${ooklaServiceLevel as string}_ACTIVE`]]: isOoklaAvailable && (isActiveMarker || isMarkerInsideActivePolygon),
		[styles.customMarker]: !isCluster,
		[String(clusterCountStyle)]: isCluster,
		[styles.clusterStyle]: isCluster,
		[styles.editingLocation]: editingAsset?.id === marker.id,
	});

	return (
		<div
			ref={markerRef}
			className={markerClassname}
			onClick={handleClickAsset}
			onContextMenu={(e) => handleRightClickMarker({
				marker,
				x: e.clientX,
				y: e.clientY,
			})}
			onMouseEnter={onMouseEnter}
			style={{
				backgroundImage,
				cursor: mapMode === EMapMode.addMarker ? 'crosshair' : 'pointer',
			}}
		>
			{clusterCount}
		</div>
	);
};

export default memo(AssetMarker, (prevProps, nextProps) => isEqual(prevProps, nextProps));
